// AddToHomeScreenTile.tsx — a quiet, dismissible Today-screen tile that invites a
// MOBILE WEB visitor to add ReNurture to their home screen, so it opens fullscreen
// from an icon (no browser address bar) instead of being typed in each time.
//
// The tile is just the nudge. On Android, if Chrome has offered a one-tap install
// (services/installPrompt), the tile fires it directly; otherwise (and on iOS) it
// opens the full AddToHomeScreenSheet, which carries the steps and — for iOS — a
// short how-to video. The same sheet is reachable from Settings, so a user who
// dismisses or misses this tile can always find it again.
//
// It renders NOTHING when it can't help or isn't wanted:
//   - inside the native Capacitor app (already "installed")
//   - already launched from the home screen (standalone display mode)
//   - on desktop (no home screen to add to)
//   - once the user has dismissed it (remembered in localStorage)
//
// House style: no emoji, no em-dash.
import React from 'react';
import { Capacitor } from '@capacitor/core';
import { Btn } from '@/components/ui';
import { Icon } from '@/components/icons';
import { AddToHomeScreenSheet } from '@/components/AddToHomeScreenSheet';
import { installPromptAvailable, onInstallPromptChange, triggerInstall } from '@/services/installPrompt';

const DISMISS_KEY = 'renurture.a2hs.dismissed';

type Platform = 'ios' | 'android' | 'other';

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  // iPadOS 13+ reports as a Mac; the touch-points check disambiguates a real iPad.
  const iOS = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1);
  if (iOS) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

/** Already opened from a home-screen icon? Then there's nothing to add. */
function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const mql = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  // iOS Safari exposes navigator.standalone instead of the display-mode media query.
  const iosStandalone = (navigator as unknown as { standalone?: boolean }).standalone === true;
  return !!mql || iosStandalone;
}

export function AddToHomeScreenTile() {
  const [dismissed, setDismissed] = React.useState<boolean>(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const [showSheet, setShowSheet] = React.useState(false);
  const [, force] = React.useReducer((x: number) => x + 1, 0);

  const platform = React.useMemo(detectPlatform, []);
  const native = Capacitor.isNativePlatform();
  const standalone = React.useMemo(isStandalone, []);

  // Re-render when Chrome offers / consumes the install prompt, so the one-tap
  // button can appear on the tile the moment it becomes available.
  React.useEffect(() => onInstallPromptChange(force), []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* private mode — fine, just won't persist */ }
    setDismissed(true);
  };

  // Don't show in the native app, when already added, on desktop, or once dismissed.
  if (native || standalone || dismissed || platform === 'other') return null;

  const canOneTap = platform === 'android' && installPromptAvailable();

  const oneTap = async () => {
    const outcome = await triggerInstall();
    if (outcome === 'accepted') dismiss();
  };

  const sub =
    platform === 'ios'
      ? 'Open it like an app, full screen. Tap below to see how.'
      : canOneTap
        ? 'Open it like an app, full screen, straight from your home screen.'
        : 'Open it like an app, full screen. Tap below to see how.';

  return (
    <>
      <div style={{ position: 'relative', marginTop: 16 }}>
        <div
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%', textAlign: 'left',
            borderRadius: 16, border: '1px solid var(--hair)',
            background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
            boxShadow: 'var(--edge), var(--sh1)', padding: '13px 15px', paddingRight: 44,
          }}
        >
          <span aria-hidden="true" style={{ display: 'flex', flexShrink: 0, marginTop: 1, color: 'var(--amber-deep)' }}>
            <Icon name="plus" size={18} />
          </span>
          <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 16, fontWeight: 500, lineHeight: 1.25, color: 'var(--ink)' }}>
              Add ReNurture to your home screen
            </span>
            <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, lineHeight: 1.5, color: 'var(--ink-soft)' }}>
              {sub}
            </span>
            <Btn
              size="md"
              onClick={() => (canOneTap ? void oneTap() : setShowSheet(true))}
              ariaLabel="Add ReNurture to your home screen"
              style={{ marginTop: 8, alignSelf: 'flex-start' }}
            >
              {canOneTap ? 'Add to home screen' : 'Show me how'}
            </Btn>
          </span>
        </div>

        <button
          type="button"
          aria-label="Hide this"
          onClick={dismiss}
          style={{
            position: 'absolute', top: 7, right: 7, padding: '3px 9px', borderRadius: 999, border: 'none',
            background: 'rgba(247,245,240,0.92)', color: 'var(--ink-soft)',
            fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, fontWeight: 600, cursor: 'pointer',
            boxShadow: '0 1px 4px rgba(0,0,0,0.14)',
          }}
        >
          Hide
        </button>
      </div>

      {showSheet && <AddToHomeScreenSheet onClose={() => setShowSheet(false)} />}
    </>
  );
}
